import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n'
import type { TrainingProgramActivation } from '../../types/api'
import { formatDateRange } from '../../utils/dateTranslations'
import { StatusBadge } from './StatusBadge'

interface ProgramAssignmentCardProps {
  activation: TrainingProgramActivation
  canManage?: boolean
  isSaved?: boolean
  isDeassigning?: boolean
  onOpen: () => void
  onChangeProgram?: () => void
  onDeassign?: () => void
}

function computeEndDate(activation: TrainingProgramActivation): Date {
  if (activation.endedAt) return new Date(activation.endedAt)
  const end = new Date(activation.startedAt)
  end.setDate(end.getDate() + activation.durationWeeks * 7)
  return end
}

/**
 * Active program assignment card for trainee/coach detail.
 * Blue accent chrome aligned with Quick Actions / training program list cards.
 */
export function ProgramAssignmentCard({
  activation,
  canManage = false,
  isSaved = true,
  isDeassigning = false,
  onOpen,
  onChangeProgram,
  onDeassign,
}: ProgramAssignmentCardProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const startDate = new Date(activation.startedAt)
  const endDate = computeEndDate(activation)
  const dateRange = formatDateRange(startDate, endDate, t)
  const weeksLabel = t.trainingPrograms.activate.durationWeeks.replace(
    '{{count}}',
    String(activation.durationWeeks),
  )
  const showFooter = canManage && (onChangeProgram || onDeassign)

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: theme.cardBg,
        border: `1px solid ${theme.cardBorder}`,
      }}
    >
      <div className="flex">
        <div
          className="w-1 shrink-0 self-stretch"
          style={{ backgroundColor: theme.buttonColor }}
          aria-hidden
        />

        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={onOpen}
            className="w-full text-left px-4 py-4 transition-opacity active:opacity-70"
          >
            {!isSaved && (
              <div className="mb-3">
                <StatusBadge
                  status={t.userDetail.programs.unsavedBadge}
                  variant="warning"
                  size="sm"
                />
              </div>
            )}

            <p
              className="text-lg font-bold leading-snug mb-3 truncate"
              style={{ color: theme.text.primary }}
            >
              {activation.programName}
            </p>

            <div
              className="flex items-stretch gap-0 rounded-xl overflow-hidden"
              style={{
                backgroundColor: theme.isDark ? 'rgba(0,136,204,0.1)' : 'rgba(0,136,204,0.06)',
              }}
            >
              <div className="flex-1 px-3.5 py-3">
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-1"
                  style={{ color: theme.text.muted }}
                >
                  {t.userDetail.programs.durationLabel}
                </div>
                <div
                  className="text-sm font-semibold leading-tight"
                  style={{ color: theme.text.primary }}
                >
                  {weeksLabel}
                </div>
              </div>
              <div
                className="w-px self-stretch my-2.5"
                style={{ backgroundColor: theme.cardBorder }}
                aria-hidden
              />
              <div className="flex-1 px-3.5 py-3">
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-1"
                  style={{ color: theme.text.muted }}
                >
                  {t.userDetail.programs.periodLabel}
                </div>
                <div
                  className="text-sm font-semibold leading-tight"
                  style={{ color: theme.text.primary }}
                >
                  {dateRange}
                </div>
              </div>
            </div>
          </button>

          {showFooter && (
            <div
              className="flex items-center justify-between px-3 py-2"
              style={{ borderTop: `1px solid ${theme.cardBorder}` }}
            >
              <div className="flex items-center gap-1 min-w-0">
                {onChangeProgram && (
                  <button
                    type="button"
                    onClick={onChangeProgram}
                    className="px-3 py-3 rounded-xl text-sm font-semibold transition-colors truncate"
                    style={{ color: theme.accent.text }}
                  >
                    {t.userDetail.programs.changeButton}
                  </button>
                )}
              </div>
              {onDeassign && (
                <button
                  type="button"
                  title={t.userDetail.programs.deassignAria}
                  onClick={onDeassign}
                  disabled={isDeassigning}
                  className="p-3 rounded-xl transition-colors disabled:opacity-40 shrink-0"
                  style={{ color: '#ef4444' }}
                  aria-label={t.userDetail.programs.deassignAria}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
